import React, { useEffect, useState } from 'react';
import './BlogTopicForm.css';
import FormActions from './components/FormActions';
import useConfirmNavigation from '../../hooks/useConfirmNavigation';

const emptyState = {
  name: '',
  slug: '',
  description: '',
  sortOrder: 0
};

const slugify = (value) => value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const BlogTopicForm = ({ initialData, onSubmit, onCancel, isSaving }) => {
  const [formState, setFormState] = useState(emptyState);
  const [slugTouched, setSlugTouched] = useState(false);
  const confirmationMessage = 'You have unsaved topic changes. Leave this form?';
  const submitLabel = initialData ? 'Update Topic' : 'Create Topic';

  useConfirmNavigation(!isSaving, confirmationMessage);

  useEffect(() => {
    if (!initialData) {
      setFormState(emptyState);
      setSlugTouched(false);
      return;
    }
    setFormState({
      name: initialData.name || '',
      slug: initialData.slug || '',
      description: initialData.description || '',
      sortOrder: initialData.sortOrder ?? 0
    });
    setSlugTouched(true);
  }, [initialData]);

  const handleChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleNameChange = (value) => {
    handleChange('name', value);
    if (!slugTouched) {
      handleChange('slug', slugify(value));
    }
  };

  const handleSlugChange = (value) => {
    setSlugTouched(true);
    handleChange('slug', slugify(value));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!onSubmit) return;
    onSubmit({
      name: formState.name.trim(),
      slug: formState.slug,
      description: formState.description.trim(),
      sortOrder: Number(formState.sortOrder) || 0
    });
  };

  const handleCancelClick = () => {
    if (!window.confirm(confirmationMessage)) {
      return;
    }
    onCancel();
  };

  return (
    <form className="blog-topic-form" onSubmit={handleSubmit}>
      <FormActions
        sticky
        isSaving={isSaving}
        onCancel={handleCancelClick}
        submitLabel={submitLabel}
      />
      <div className="form-group">
        <label>Name</label>
        <input value={formState.name} onChange={(event) => handleNameChange(event.target.value)} required />
      </div>
      <div className="form-group">
        <label>Slug</label>
        <input value={formState.slug} onChange={(event) => handleSlugChange(event.target.value)} required />
        <p className="helper-text">Used in URLs. Only lowercase letters, numbers, and hyphens.</p>
      </div>
      <div className="form-group">
        <label>Description</label>
        <textarea value={formState.description} onChange={(event) => handleChange('description', event.target.value)} rows={3} />
      </div>
      <div className="form-group">
        <label>Sort Order</label>
        <input
          type="number"
          value={formState.sortOrder}
          onChange={(event) => handleChange('sortOrder', event.target.value)}
          min="0"
        />
      </div>

      <FormActions
        variant="footer"
        isSaving={isSaving}
        onCancel={handleCancelClick}
        submitLabel={submitLabel}
      />
    </form>
  );
};

export default BlogTopicForm;
